import { useFormik } from 'formik';
import { InferType, object, string } from 'yup';
import { toast } from 'react-toastify';

import { Input } from '../../../components/common/Form';
import ReservationOrderInputLabel from '../../../components/ReservationOrderInputLabel';
import RestaurantWizardHeader from '../RestaurantWizard/RestaurantWizardHeader';
import { useBrandContext } from '../../../contexts/BrandContext/BrandContext';
import { updateBrandGiftCardsUrl } from '../../../api/brand';
import { handleHttpsInput, isValidWebUrl } from '../../../utils/general';
import BrandProfileActions from './BrandProfileActions';

const GiftCardInputSchema = object({
  giftCardUrl: string()
    .default('')
    .test(
      'valid-link',
      'Enter a valid link, like https://example.com/gift-cards.',
      (value) => !value || isValidWebUrl(value)
    )
});

const BrandGiftCardsPage = () => {
  const { currentBrand, refreshCurrentBrand } = useBrandContext();

  const initialValues = {
    giftCardUrl: currentBrand?.giftCardsUrl ?? ''
  };

  const handleOnSubmit = async (value: InferType<typeof GiftCardInputSchema>) => {
    if (!currentBrand?.id) {
      toast.error('Unable to save gift cards URL: no brand selected.', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    try {
      await updateBrandGiftCardsUrl(currentBrand.id, value.giftCardUrl);
      await refreshCurrentBrand();

      toast.success('Successfully Updated!', {
        position: toast.POSITION.TOP_RIGHT
      });
    } catch (error) {
      console.error('Error saving gift cards URL:', error);
      toast.error('Failed to update gift cards URL.', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: GiftCardInputSchema,
    onSubmit: handleOnSubmit,
    enableReinitialize: true
  });

  const handleInputChange = (name: string, value: string) => {
    formik.setFieldValue(name, handleHttpsInput(value));
  };

  return (
    <div className="restaurant-wizard-container reservation-order-container">
      <div className="restaurant-wizard-hero-background" aria-hidden="true" />
      <div className="restaurant-wizard-scroll-body">
        <RestaurantWizardHeader title="GIFT CARDS" />
        <form className="restaurant-wizard-content reservation-order-content" onSubmit={formik.handleSubmit}>
          <div className="reservation-order-page">
            <div className="reservation-order-inputs">
              <Input
                name="giftCardUrl"
                label={
                  <ReservationOrderInputLabel
                    label="gift cards"
                    text="Enter the link at which guests can purchase gift cards."
                  />
                }
                className="reservation-order-input"
                onChange={(event: any) => handleInputChange('giftCardUrl', event.target.value)}
                onBlur={formik.handleBlur}
                value={formik.values.giftCardUrl}
                touched={formik.touched.giftCardUrl}
                error={formik.errors.giftCardUrl}
              />
            </div>
            <BrandProfileActions submit isSaving={formik.isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandGiftCardsPage;
